import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, Lock, AlertTriangle, CheckCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface RuminationDetectionProps {
  tier: 'free' | 'premium' | 'pro';
}

export function RuminationDetection({ tier }: RuminationDetectionProps) {
  const [ruminationCount, setRuminationCount] = useState(0);
  const [lastCheckinTimestamp, setLastCheckinTimestamp] = useState<string | null>(null);
  const [interventionActive, setInterventionActive] = useState(false);

  const isPro = tier === 'pro';

  useEffect(() => {
    detectRumination();
  }, []);

  const detectRumination = () => {
    const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    const journalEntries = JSON.parse(localStorage.getItem('tension_journal_entries') || '[]');
    const anxietyLogs = JSON.parse(localStorage.getItem('tension_anxiety_tracker') || '[]');

    // Get activity from last 24 hours
    const last24h = new Date();
    last24h.setHours(last24h.getHours() - 24);

    const recentCheckins = checkins.filter((c: any) => 
      new Date(c.timestamp) >= last24h
    );

    const recentJournal = journalEntries.filter((j: any) => 
      new Date(j.timestamp) >= last24h
    );

    const recentAnxiety = anxietyLogs.filter((a: any) => 
      new Date(a.timestamp) >= last24h
    );

    // Rumination detection logic
    let ruminationScore = 0;

    // Multiple check-ins in short period
    if (recentCheckins.length > 3) {
      ruminationScore += 2;
    }

    // Multiple anxiety logs
    if (recentAnxiety.length > 2) {
      ruminationScore += 3;
    }

    // Repeated journal themes (check for similar words)
    if (recentJournal.length >= 2) {
      const notes = recentJournal.map((j: any) => j.note.toLowerCase());
      const commonWords = ['didn\'t', 'hasn\'t', 'ignored', 'worried', 'anxious', 'why'];
      const wordCounts = commonWords.map(word => 
        notes.filter((note: string) => note.includes(word)).length
      );
      const maxCount = Math.max(...wordCounts);
      if (maxCount >= 2) {
        ruminationScore += 2;
      }
    }

    // High RTI with frequent checks
    const avgRTI = recentCheckins.length > 0
      ? recentCheckins.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / recentCheckins.length
      : 0;
    
    if (avgRTI > 60 && recentCheckins.length > 2) {
      ruminationScore += 2;
    }

    setRuminationCount(ruminationScore);
    
    if (recentCheckins.length > 0) {
      setLastCheckinTimestamp(recentCheckins[recentCheckins.length - 1].timestamp);
    }

    // Auto-trigger intervention if rumination detected
    if (ruminationScore >= 5 && !interventionActive) {
      setInterventionActive(true);
    }
  };

  const getRuminationLevel = (): 'none' | 'mild' | 'moderate' | 'severe' => {
    if (ruminationCount >= 7) return 'severe';
    if (ruminationCount >= 5) return 'moderate';
    if (ruminationCount >= 3) return 'mild';
    return 'none';
  };

  const level = getRuminationLevel();

  const getInterventionProtocol = () => {
    switch (level) {
      case 'severe':
        return {
          title: '🚨 Severe Rumination Detected',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          actions: [
            {
              title: 'Immediate Circuit Breaker',
              description: 'Close this app for 4 hours. Set a timer. Ruminating won\'t change the outcome.',
              action: 'app_break',
            },
            {
              title: 'Physical Intervention',
              description: 'Go for a 20-minute walk, run, or workout. Move your body to shift your brain state.',
              action: 'exercise',
            },
            {
              title: 'Social Redirect',
              description: 'Text a friend (not about your relationship). Get out of your own head.',
              action: 'social',
            },
          ],
        };
      case 'moderate':
        return {
          title: '⚠️ Rumination Pattern Detected',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          actions: [
            {
              title: 'Distraction Protocol',
              description: 'Engage in a 30-minute absorbing activity: read, watch something, play a game.',
              action: 'distraction',
            },
            {
              title: 'Reframe Exercise',
              description: 'Write down 3 alternative explanations for their behavior (not worst-case scenarios).',
              action: 'reframe',
            },
            {
              title: 'Time-Box Worry',
              description: 'Set a 10-minute timer to worry freely. When it ends, move on.',
              action: 'timebox',
            },
          ],
        };
      case 'mild':
        return {
          title: '💡 Early Rumination Signs',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          actions: [
            {
              title: 'Awareness Check',
              description: 'Notice: "I\'m ruminating." Just labeling it reduces its power.',
              action: 'awareness',
            },
            {
              title: 'Focus Shift',
              description: 'Name 5 things you can see, 4 you can touch, 3 you can hear. Ground yourself.',
              action: 'grounding',
            },
            {
              title: 'Productive Action',
              description: 'Choose one small task to complete right now. Shift from thinking to doing.',
              action: 'action',
            },
          ],
        };
      default:
        return null;
    }
  };

  const protocol = getInterventionProtocol();

  const handleActionComplete = (action: string) => {
    toast.success('Great job! You broke the rumination cycle.');
    
    // Log intervention completion
    const completions = JSON.parse(localStorage.getItem('tension_rumination_interventions') || '[]');
    completions.push({
      timestamp: new Date().toISOString(),
      level,
      action,
    });
    localStorage.setItem('tension_rumination_interventions', JSON.stringify(completions));

    // Reset count
    setInterventionActive(false);
    setRuminationCount(0);
  };

  const getStats = () => {
    const interventions = JSON.parse(localStorage.getItem('tension_rumination_interventions') || '[]');
    const last30Days = interventions.filter((i: any) => {
      const date = new Date(i.timestamp);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return date >= thirtyDaysAgo;
    });

    return {
      total: last30Days.length,
      byLevel: {
        severe: last30Days.filter((i: any) => i.level === 'severe').length,
        moderate: last30Days.filter((i: any) => i.level === 'moderate').length,
        mild: last30Days.filter((i: any) => i.level === 'mild').length,
      },
    };
  };

  const stats = getStats();

  if (!isPro) {
    return (
      <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-100 rounded-lg">
            <Lock className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
              <h3 className="font-medium text-neutral-900">Rumination Detection</h3>
            </div>
            <p className="text-sm text-neutral-600 mb-3">
              AI detects when you're stuck in thought loops and provides immediate intervention protocols to break the cycle.
            </p>
            <div className="tension-glow bg-white/60 rounded-lg p-3 mb-3">
              <p className="text-xs text-neutral-700 mb-2">
                <strong>Rumination triggers detected:</strong>
              </p>
              <div className="space-y-1 text-xs text-neutral-600">
                <p>• Multiple check-ins in 24 hours</p>
                <p>• Repeated anxiety logging</p>
                <p>• Same themes in journal entries</p>
                <p>• High RTI with frequent app usage</p>
              </div>
              <p className="text-xs text-neutral-700 mt-3 mb-1">
                <strong>Interventions provided:</strong>
              </p>
              <p className="text-xs text-neutral-600">
                Circuit breakers, distraction protocols, grounding exercises, and reframe techniques.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => window.location.href = '/settings'}
              className="gap-2"
            >
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-indigo-600" />
              <h3 className="font-medium text-neutral-900">Rumination Detection</h3>
              <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={detectRumination}
            >
              Refresh
            </Button>
          </div>

          {level === 'none' ? (
            <div className="tension-glow bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-900">No Rumination Detected</p>
                  <p className="text-xs text-green-700">
                    Your thought patterns are healthy. Keep it up!
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {protocol && (
                <div className={`tension-glow ${protocol.bgColor} border ${protocol.borderColor} rounded-lg p-4`}>
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className={`w-6 h-6 ${protocol.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1">
                      <h4 className={`font-medium ${protocol.color} mb-1`}>{protocol.title}</h4>
                      <p className="text-sm text-neutral-700">
                        You've checked in {ruminationCount >= 5 ? 'multiple times' : 'frequently'} in the last 24 hours. 
                        This suggests you're ruminating.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-neutral-900">
                      Choose one intervention now:
                    </p>
                    {protocol.actions.map((action, idx) => (
                      <div
                        key={idx}
                        className="tension-glow bg-white rounded-lg p-4 border border-neutral-200"
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-neutral-900 mb-1">
                              {action.title}
                            </p>
                            <p className="text-sm text-neutral-700">
                              {action.description}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleActionComplete(action.action)}
                          className="w-full mt-2"
                        >
                          I've Done This
                        </Button>
                      </div>
                    ))}
                  </div>

                  {level === 'severe' && (
                    <div className="tension-glow bg-red-100 border border-red-300 rounded-lg p-3 mt-4">
                      <p className="text-sm text-red-900 font-medium mb-1">
                        ⚠️ Critical: Close This App
                      </p>
                      <p className="text-xs text-red-800">
                        Continued checking will worsen anxiety. Give yourself permission to step away for at least 4 hours.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Stats */}
          {stats.total > 0 && (
            <div className="tension-glow bg-white/60 rounded-lg p-4 border border-indigo-200">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-indigo-600" />
                <h4 className="text-sm font-medium text-neutral-900">
                  Last 30 Days: {stats.total} Interventions
                </h4>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-yellow-50 rounded-lg p-2 border border-yellow-200">
                  <p className="text-lg font-light text-yellow-900">{stats.byLevel.mild}</p>
                  <p className="text-xs text-yellow-700">Mild</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-2 border border-orange-200">
                  <p className="text-lg font-light text-orange-900">{stats.byLevel.moderate}</p>
                  <p className="text-xs text-orange-700">Moderate</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 border border-red-200">
                  <p className="text-lg font-light text-red-900">{stats.byLevel.severe}</p>
                  <p className="text-xs text-red-700">Severe</p>
                </div>
              </div>
              {stats.byLevel.severe >= 3 && (
                <p className="text-xs text-orange-700 mt-3 text-center">
                  ⚠️ Frequent severe rumination detected. Consider speaking with a therapist.
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
